/**
 * Reports Homepage
 *
 * Grid of all reports grouped into three sections:
 *   Required  — always visible, cannot be hidden
 *   Default   — shown by default, admin can turn off
 *   Custom    — user-created reports (links to the library)
 *
 * Routes: /prioritization/reports
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  RefreshCw,
  FileText,
  ClipboardList,
  ShieldCheck,
  Plus,
  Pin,
  Folder,
  BookOpen,
  Activity,
  Target,
  Filter,
  Camera,
  type LucideIcon,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { usePrioritySetStore } from '../../../stores/prioritization/prioritySetStore';
import { useReportSettingsStore } from '../../../stores/prioritization/reportSettingsStore';
import { useSavedReportsStore } from '../../../stores/prioritization/savedReportsStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useModuleGateResolver } from '../../../lib/moduleGate';
import { isAdminRole } from '../../../types/prioritization/roles';
import { resolveRoleReportDefaults, selectHomeReports } from '../../../lib/roleReportDefaults';
import { UI_LABELS } from '../../../lib/terminology';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { migrateLocalCustomReports } from '../../../lib/migrateLocalCustomReports';
import { listReportSnapshots, snapshotPath, type ReportSnapshot } from '../../../lib/reportSnapshotsService';
import { formatWhen } from '../../../lib/calendarDate';
import {
  HOME_REPORTS,
  REPORT_DOMAIN_ORDER,
  type ReportAuthority,
  type ReportDomain,
} from '../../../lib/reportSettingsService';

// ── Per-report display metadata (icon + accent) ──────────────────────────────
// Keyed by report key; falls back to a domain default. Kept here (not in the
// registry service) so that service stays a plain, React-free data module.

const REPORT_ICONS: Record<string, { icon: LucideIcon; iconBg: string; iconColor: string }> = {
  'pipeline-health':     { icon: BarChart3,     iconBg: 'bg-[var(--slate-dim)]', iconColor: 'text-[var(--text-ter)]' },
  'session-reports':     { icon: ClipboardList, iconBg: 'bg-[var(--green-dim)]',  iconColor: 'text-[var(--green)]' },
  'cycle-execution':     { icon: RefreshCw,     iconBg: 'bg-[var(--slate-dim)]', iconColor: 'text-[var(--text-ter)]' },
  'cycle-reports':       { icon: ShieldCheck,   iconBg: 'bg-[var(--slate-dim)]', iconColor: 'text-[var(--text-ter)]' },
  'relationship-health': { icon: Activity,      iconBg: 'bg-[var(--mustard-dim)]',  iconColor: 'text-[var(--mustard)]' },
  'intake-clarity':      { icon: Filter,        iconBg: 'bg-[var(--slate-dim)]',    iconColor: 'text-[var(--text-ter)]' },
  'strategy-alignment':  { icon: Target,        iconBg: 'bg-[var(--green-dim)]',  iconColor: 'text-[var(--green)]' },
};

const DOMAIN_FALLBACK_ICON: Record<ReportDomain, { icon: LucideIcon; iconBg: string; iconColor: string }> = {
  items:         { icon: BarChart3, iconBg: 'bg-[var(--slate-dim)]', iconColor: 'text-[var(--text-ter)]' },
  sessions:      { icon: FileText,  iconBg: 'bg-[var(--slate-dim)]',   iconColor: 'text-[var(--text-ter)]' },
  cycles:        { icon: RefreshCw, iconBg: 'bg-[var(--slate-dim)]', iconColor: 'text-[var(--text-ter)]' },
  strategy:      { icon: ShieldCheck, iconBg: 'bg-[var(--green-dim)]', iconColor: 'text-[var(--green)]' },
  relationships: { icon: Activity,  iconBg: 'bg-[var(--mustard-dim)]',  iconColor: 'text-[var(--mustard)]' },
  monitoring:    { icon: Activity,  iconBg: 'bg-[var(--slate-dim)]', iconColor: 'text-[var(--text-ter)]' },
  decisions:     { icon: ShieldCheck, iconBg: 'bg-[var(--slate-dim)]', iconColor: 'text-[var(--text-ter)]' },
  custom:        { icon: Folder,    iconBg: 'bg-[var(--hover-tint)]',  iconColor: 'text-[var(--text-ter)]' },
};

// ── Authority badge ──────────────────────────────────────────────────────────
// Canon 08 §3.5 authority tiers. `none` renders no badge.

const AUTHORITY_BADGE: Record<Exclude<ReportAuthority, 'none'>, { label: string; className: string }> = {
  informational: { label: 'Informational', className: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
  advisory:      { label: 'Advisory',      className: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  authoritative: { label: 'Authoritative', className: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
};

function AuthorityBadge({ authority }: { authority: ReportAuthority }) {
  if (authority === 'none') return null;
  const { label, className } = AUTHORITY_BADGE[authority];
  return (
    <span
      title={`Canon authority tier: ${label}`}
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${className}`}
    >
      {label}
    </span>
  );
}

// ── Report card ──────────────────────────────────────────────────────────────

function ReportCard({
  icon,
  iconBg,
  iconColor,
  name,
  description,
  stat,
  href,
  required,
  authority,
}: {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  name: string;
  description: string;
  stat?: string;
  href: string;
  required?: boolean;
  authority?: ReportAuthority;
}) {
  const Icon = icon;
  return (
    <Link
      to={href}
      className="group flex flex-col bg-[var(--bg-raised)] border border-[var(--rule)] p-5 hover:border-primary-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9  ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-4.5 w-4.5 ${iconColor}`} style={{ width: '18px', height: '18px' }} />
        </div>
        <div className="flex items-center gap-1">
          <AuthorityBadge authority={authority ?? 'none'} />
          {required && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] text-[10px] font-semibold rounded-full">
              <Pin className="h-2.5 w-2.5" /> Required
            </span>
          )}
        </div>
      </div>
      <h3 className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-primary-700 transition-colors">{name}</h3>
      <p className="text-xs text-[var(--text-ter)] mt-1 flex-1 leading-relaxed">{description}</p>
      {stat && <p className="text-xs text-[var(--text-ter)] mt-3 font-medium">{stat}</p>}
    </Link>
  );
}

// ── Section header ───────────────────────────────────────────────────────────

function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wider">{title}</h2>
        {description && <p className="text-xs text-[var(--text-ter)] mt-0.5">{description}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export function ReportsIndex() {
  const workspaceId = getCurrentWorkspaceId() ?? 'default';
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? workspaceId;
  const { fetchSettings, getSetting } = useReportSettingsStore();

  useEffect(() => {
    if (workspaceId) fetchSettings(workspaceId);
  }, [workspaceId, fetchSettings]);

  const isVisible  = (key: string) => getSetting(key).isVisible;
  const isRequired = (key: string) => getSetting(key).isRequired;

  // Role-aware visibility (Phase 3). `can` enforces each report's requiredCapability
  // (Layer A); role defaults tailor which of the allowed reports show by default (Layer B).
  const can = useUserStore((s) => s.can);
  const assignedRoleNames = useUserStore((s) => s.assignedRoleNames);
  const currentRole = useUserStore((s) => s.currentRole);
  const permissionsLoaded = useUserStore((s) => s.permissionsLoaded);
  const [showAll, setShowAll] = useState(false);

  const isAdmin =
    (currentRole !== null && isAdminRole(currentRole)) ||
    assignedRoleNames.some((n) => n === 'workspace-admin' || n === 'tenant-admin' || n === 'platform-admin' || n === 'system-admin');

  const roleDefaults = useMemo(
    () => resolveRoleReportDefaults(assignedRoleNames, isAdmin),
    [assignedRoleNames, isAdmin]
  );

  const cycles       = useDegapProcessStore((s) => s.cycles);
  const sessions     = useSessionStore((s) => s.sessions);
  const items        = useItemsStore((s) => s.items);
  const itemPools    = useItemPoolsStore((s) => s.itemPools);
  const loops        = useCatchballLoopStore((s) => s.loops);
  const prioritySets = usePrioritySetStore((s) => s.prioritySets);

  // Custom reports (Supabase-backed). Fetch, then import any legacy localStorage
  // reports (once). Scoped to this workspace and item-entity reports.
  const allSavedReports = useSavedReportsStore((s) => s.reports);
  const fetchSavedReports = useSavedReportsStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchSavedReports();
      if (!cancelled) migrateLocalCustomReports(currentWorkspaceId);
    })();
    return () => { cancelled = true; };
  }, [fetchSavedReports, currentWorkspaceId]);

  const [snapshots, setSnapshots] = useState<ReportSnapshot[]>([]);
  useEffect(() => {
    let cancelled = false;
    void listReportSnapshots({ limit: 9 }).then((rows) => { if (!cancelled) setSnapshots(rows); });
    return () => { cancelled = true; };
  }, [currentWorkspaceId]);

  const customReports = useMemo(
    () =>
      allSavedReports
        .filter(
          (r) =>
            r.entityType === 'items' &&
            (!r.workspaceId || r.workspaceId === currentWorkspaceId)
        )
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [allSavedReports, currentWorkspaceId]
  );

  const stats = useMemo(() => ({
    activeCycles:        cycles.filter((c) => c.status === 'active').length,
    completedCycles:     cycles.filter((c) => c.status === 'completed').length,
    totalCycles:         cycles.length,
    publishedSessions:   sessions.filter((s) => s.state === 'PUBLISHED').length,
    sessionsWithOutput:  sessions.filter((s) => s.rankingOutput).length,
    totalSessions:       sessions.length,
    totalItems:          items.length,
    prioritizedItems:    items.filter((c) => c.detailedState === 'PRIORITIZED').length,
    activeItemPools:     itemPools.filter((fl) => fl.status === 'active').length,
    totalItemPools:      itemPools.length,
    loopsWithSynthesis:  loops.filter((l) => l.synthesis).length,
    totalLoops:          loops.length,
    ratifiedPrioritySets: prioritySets.filter((ps) => ps.state === 'ratified' || ps.state === 'published').length,
    totalPrioritySets:   prioritySets.length,
  }), [cycles, sessions, items, itemPools, loops, prioritySets]);

  // Per-report summary stat, keyed by registry key.
  const statFor: Record<string, string> = {
    'pipeline-health':     `${stats.totalItems} items tracked`,
    'session-reports':     `${stats.totalSessions} sessions · ${stats.sessionsWithOutput} with output`,
    'cycle-execution':     `${stats.totalCycles} cycles · ${stats.activeCycles} active`,
    'cycle-reports':       `${stats.totalCycles} cycles · ${stats.ratifiedPrioritySets} ratified`,
    'relationship-health': `${stats.totalItems} items`,
  };

  // Apply the layered visibility: workspace visibility + capability floor (Layer A)
  // + role-tailored default / show-all (Layer B). `selectHomeReports` is pure and
  // fails open while permissions load so the home never blanks.
  const isModuleOn = useModuleGateResolver();
  const selectArgs = {
    canView: can,
    permissionsLoaded,
    roleDefaults,
    isVisible,
    isRequired,
    isModuleOn,
  };
  const selectedReports = selectHomeReports({ reports: HOME_REPORTS, showAll, ...selectArgs });
  const roleCount = selectHomeReports({ reports: HOME_REPORTS, showAll: false, ...selectArgs }).length;
  const allCount  = selectHomeReports({ reports: HOME_REPORTS, showAll: true, ...selectArgs }).length;
  const hasMoreReports = allCount > roleCount;

  // Build the home cards from the selected set, grouped by domain. Every card here
  // resolves to a real landing page.
  const homeDomains = REPORT_DOMAIN_ORDER.map((group) => {
    const reports = selectedReports
      .filter((r) => r.domain === group.domain)
      .map((r) => {
        const accent = REPORT_ICONS[r.key] ?? DOMAIN_FALLBACK_ICON[r.domain];
        return {
          key: r.key,
          icon: accent.icon,
          iconBg: accent.iconBg,
          iconColor: accent.iconColor,
          name: r.name,
          description: r.description,
          stat: statFor[r.key],
          href: r.href,
          required: isRequired(r.key),
          authority: r.authority,
        };
      });
    return { ...group, reports };
  }).filter((g) => g.reports.length > 0);

  return (
    <div className="space-y-8">
      <PageHeader
        icon={<BarChart3 className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Reports"
        subtitle="Organized by domain — with a custom report library"
        actions={
          <Link
            to="/prioritization/reports/custom/new"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Report
          </Link>
        }
      />

      {/* Stats strip */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-[var(--rule)]">
          {[
            { label: 'Cycles',   value: stats.totalCycles,   sub: `${stats.activeCycles} active` },
            { label: 'Sessions', value: stats.totalSessions,  sub: `${stats.publishedSessions} published` },
            { label: 'Items',    value: stats.totalItems,     sub: `${stats.prioritizedItems} prioritized` },
            { label: UI_LABELS.itemPools, value: stats.totalItemPools, sub: `${stats.activeItemPools} active` },
          ].map(({ label, value, sub }) => (
            <div key={label} className="px-5 py-3">
              <p className="text-xs text-[var(--text-ter)]">{label}</p>
              <p className="text-base font-semibold text-[var(--text-pri)] tabular-nums">{value}</p>
              <p className="text-xs text-[var(--text-ter)]">{sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Role-tailored view toggle */}
      {(hasMoreReports || showAll) && (
        <div className="flex items-center justify-between -mb-3">
          <p className="text-xs text-[var(--text-ter)]">
            {showAll
              ? 'Showing all reports available to you.'
              : 'Showing reports suited to your role.'}
          </p>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
          >
            {showAll ? 'Show reports for my role' : `Show all reports (${allCount})`}
          </button>
        </div>
      )}

      {/* Domain groups */}
      {homeDomains.map((group) => (
        <section key={group.domain}>
          <SectionHeader title={group.label} description={group.description} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {group.reports.map(({ key, ...card }) => (
              <ReportCard key={key} {...card} />
            ))}
          </div>
        </section>
      ))}

      {/* Snapshots — reports saved as they were at one moment (RP-7) */}
      {snapshots.length > 0 && (
        <section>
          <SectionHeader title="Snapshots" description="Reports saved as they were at one moment. Each has its own address; anyone signed in here can open it." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {snapshots.map((snap) => (
              <Link
                key={snap.id}
                to={snapshotPath(snap.id)}
                className="group flex flex-col bg-[var(--bg-raised)] border border-[var(--rule)] p-5 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <div className="w-9 h-9 bg-[var(--hover-tint)] flex items-center justify-center mb-3">
                  <Camera className="text-[var(--text-ter)]" style={{ width: '18px', height: '18px' }} />
                </div>
                <h3 className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-primary-700 transition-colors truncate">{snap.name}</h3>
                <p className="text-xs text-[var(--text-ter)] mt-1 flex-1">
                  {snap.config.reportName} · {snap.config.rangeLabel}{snap.config.segmentLabel ? ` · by ${snap.config.segmentLabel}` : ''}
                </p>
                <p className="text-xs text-[var(--text-ter)] mt-3">Taken {formatWhen(snap.createdAt, { withYear: true })}{snap.createdByName ? ` by ${snap.createdByName}` : ''}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Custom */}
      <section>
        <SectionHeader
          title="Custom"
          description="Reports you've built and saved from any view."
          action={
            <div className="flex items-center gap-2">
              <Link
                to="/prioritization/reports/custom"
                className="text-xs text-primary-600 hover:underline font-medium"
              >
                Browse all
              </Link>
              <Link
                to="/prioritization/reports/custom/new"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> New
              </Link>
            </div>
          }
        />
        {customReports.length === 0 ? (
          <div className="bg-[var(--bg-raised)] border border-dashed border-[var(--rule)] p-10 text-center">
            <BookOpen className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm font-medium text-[var(--text-sec)]">No custom reports yet</p>
            <p className="text-xs text-[var(--text-ter)] mt-1 mb-4">Build a report from any item view, filter it, then save it here.</p>
            <Link
              to="/prioritization/reports/custom/new"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> Create your first report
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {customReports.slice(0, 6).map((r) => (
              <Link
                key={r.id}
                to={`/prioritization/reports/custom/${r.id}`}
                className="group flex flex-col bg-[var(--bg-raised)] border border-[var(--rule)] p-5 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <div className="w-9 h-9 bg-[var(--hover-tint)] flex items-center justify-center mb-3">
                  <Folder className="h-4.5 w-4.5 text-[var(--text-ter)]" style={{ width: '18px', height: '18px' }} />
                </div>
                <h3 className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-primary-700 transition-colors truncate">{r.name}</h3>
                {r.description && <p className="text-xs text-[var(--text-ter)] mt-1 flex-1 line-clamp-2">{r.description}</p>}
                <p className="text-xs text-[var(--text-ter)] mt-3">
                  Updated {new Date(r.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </Link>
            ))}
            {customReports.length > 6 && (
              <Link
                to="/prioritization/reports/custom"
                className="flex flex-col items-center justify-center bg-[var(--surface)] border border-dashed border-[var(--rule)] p-5 hover:border-primary-300 transition-colors text-sm text-[var(--text-ter)] hover:text-primary-700"
              >
                +{customReports.length - 6} more reports →
              </Link>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default ReportsIndex;
